/**
 * 流式正文的匀速显示。
 *
 * 服务端每 250ms 左右才推送一次累计快照（且会去掉 Markdown 标记，已显示部分偶尔被改写），
 * 直接渲染会一段一段地跳。这里把“已收到的全文”和“已显示的位置”分开，位置按帧平滑前进：
 * 落后越多走得越快，但时间常数比推送间隔长，所以两次推送之间不会走完就停；
 * 落后很少时也保持最低速度，避免流结束后还拖着慢慢打字。
 */

/** 落后量的时间常数（毫秒）：略长于推送间隔，让一批新字刚好铺满到下一批到达。 */
const CATCH_UP_MS = 380;
/** 最低速度（字/秒），保证尾巴很快收完。 */
const MIN_CHARS_PER_SECOND = 45;
/** 单帧时间上限：标签页切回等长间隔不会一次跳很远。 */
const MAX_FRAME_MS = 100;

/** 下一帧的显示位置（可为小数）；不超过全文长度。 */
export function nextRevealPosition(total: number, position: number, elapsedMs: number): number {
  if (position >= total) return total;
  const frame = Number.isFinite(elapsedMs) ? Math.min(Math.max(elapsedMs, 0), MAX_FRAME_MS) : 16;
  const chase = (total - position) * (1 - Math.exp(-frame / CATCH_UP_MS));
  const floor = MIN_CHARS_PER_SECOND * frame / 1000;
  return Math.min(total, position + Math.max(chase, floor));
}

/** 按位置截取已显示文本；不把代理对拆开。 */
export function revealed(text: string, position: number): string {
  let end = Math.min(text.length, Math.floor(position));
  if (end > 0 && end < text.length) {
    const last = text.charCodeAt(end - 1);
    if (last >= 0xd800 && last <= 0xdbff) end += 1;
  }
  return text.slice(0, end);
}

export interface SmoothTextOptions {
  /** 已显示文本变化时回调。 */
  onChange: (shown: string) => void;
  /** 为 true 时不做动画，直接显示全文（减少动态效果、无 rAF 环境）。 */
  instant: () => boolean;
}

/** 单段文本的逐字显示器。全文被改写时保持当前位置继续，不会从头再来。 */
export class SmoothText {
  private target = '';
  private position = 0;
  private shownText = '';
  private frame: null | number = null;
  private last = 0;

  constructor(private readonly options: SmoothTextOptions) {}

  /** 已显示的文本。 */
  get text(): string { return this.shownText; }

  /** 是否还有没显示完的内容。 */
  get pending(): boolean { return this.position < this.target.length; }

  /** 更新全文。位置只增不减：全文变短时收在末尾，被改写时按位置继续。 */
  set(text: string): void {
    if (text === this.target) return;
    this.target = text;
    if (this.options.instant()) {
      this.position = text.length;
      this.cancel();
    } else {
      this.position = Math.min(this.position, text.length);
    }
    this.publish();
    this.schedule();
  }

  /** 清空并停止动画。 */
  reset(): void {
    this.cancel();
    this.target = '';
    this.position = 0;
    this.publish();
  }

  /** 停止动画但保留状态（组件卸载）。 */
  dispose(): void { this.cancel(); }

  private publish(): void {
    const next = revealed(this.target, this.position);
    if (next === this.shownText) return;
    this.shownText = next;
    this.options.onChange(next);
  }

  private cancel(): void {
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  private schedule(): void {
    if (this.frame !== null || !this.pending) return;
    this.last = performance.now();
    this.next();
  }

  private next(): void {
    this.frame = requestAnimationFrame((now) => this.tick(now));
  }

  private tick(now: number): void {
    this.frame = null;
    this.position = nextRevealPosition(this.target.length, this.position, now - this.last);
    this.last = now;
    this.publish();
    if (this.pending) this.next();
  }
}

/** 用户要求减少动态效果，或环境没有 rAF（测试、极老内核）时不做动画。 */
export function prefersInstantText(): boolean {
  if (typeof requestAnimationFrame !== 'function') return true;
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}
